import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    input,
    linkedSignal,
    signal,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { AlertService } from "../../../services/alert.service";
import { EventProfile, EventService } from "../../../services/event.service";
import { ListDataParams2, ListService } from "../../../services/list.service";
import { Item } from "../../../pojo/Item";
import { Bind } from "../../../decorators/bind.decorator";
import { S25LoadingInlineStaticComponent } from "../../s25-loading/s25.loading.inline.static.component";
import { Action, S25ListDataComponent } from "../../s25-table-list/s25.list.data.component";
import { QuickSearch, S25QuickSearchComponent } from "../../s25-quick-search/s25.quick.search.component";
import { S25EditableModule } from "../../s25-editable/s25.editable.module";
import { S25Util } from "../../../util/s25-util";
import { S25Assert } from "../../../util/s25.assert";
import { s25Resource } from "../../../util/s25.resource";
import { AddRemoveUtil } from "../../../standalone/more-actions/edit/add.remove.util";
import { S25TableComponent } from "../../s25-table/s25.table.component";

@Component({
    selector: "s25-ng-more-actions-related",
    template: `
        @if (requiresProfile() && !profiles()) {
            <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
        } @else if (profiles() && profiles()!.length > 1) {
            <label class="cn-label profile-picker" for="chooseProfile">
                <span>Please select the segment whose bindings you want to manage:</span>
                <select id="chooseProfile" class="c-selectInput" [(ngModel)]="chosenProfile">
                    @for (profile of profiles(); track profile.id) {
                        <option [ngValue]="profile">{{ profile.profile_name }}</option>
                    }
                </select>
                @if (chosenProfile()) {
                    <a (click)="chosenProfile.set(undefined)" href="javascript:void(0)">Clear</a>
                }
            </label>
        }
        @if (chosenProfile() || !requiresProfile()) {
            @if (itemName.resource.isLoading()) {
                <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
            } @else {
                <p class="moreActionsHeader">
                    <strong class="moreActionsEventName">{{ itemNameWithLocator() }}</strong>
                    {{ itemIds()?.length ? lang().inSet : lang().notInSet }}
                    @if (addRemoveType() === "boundReservations" && editMode()) {
                        <button
                            class="aw-button aw-button--outline c-margin-left--single"
                            (click)="editMode.set(false)"
                        >
                            Return to Set
                        </button>
                    }
                </p>
                @if (setMeta.value()) {
                    <div class="cn-label">
                        <label for="relatedSetName">Related Set Name</label>
                        <s25-ng-editable-text
                            [alwaysEditing]="false"
                            [allowEmpty]="true"
                            [val]="$safeNavigationMigration(setMeta.value()?.itemName)"
                            [hasCommit]="true"
                            [max]="40"
                            [onCommit]="saveSetName"
                            [fieldId]="'relatedSetName'"
                            [readonlyTemplate]="readonlySetName"
                        ></s25-ng-editable-text>

                        @if (editMode()) {
                            <button
                                class="aw-button aw-button--outline c-margin-left--single"
                                (click)="editMode.set(false)"
                            >
                                Return to Set
                            </button>
                        }
                    </div>

                    <ng-template #readonlySetName let-setName>
                        @if (setName) {
                            {{ setName }}
                        } @else {
                            <span class="ng-item-name__set-placeholder">+ Add Set Name</span>
                        }
                    </ng-template>
                }

                @if (!editMode()) {
                    <div class="actionItems">
                        <button
                            class="aw-button aw-button--primary"
                            id="add-events-to-set"
                            (click)="editMode.set('add')"
                        >
                            Add Events to Set
                        </button>

                        @if (itemIds()?.length) {
                            <button
                                class="aw-button aw-button--primary"
                                (click)="editMode.set('remove')"
                                id="remove-events-from-set"
                            >
                                Remove Events from Set
                            </button>
                        }
                    </div>
                }

                @if (errorMsg()) {
                    <p class="ngRed">{{ errorMsg() }}</p>
                }

                @if (editMode() === "add") {
                    <s25-ng-quick-search
                        [(searchTerm)]="searchTerm"
                        [(selectedSearch)]="selectedSearch"
                        (searchSubmit)="commitSearch()"
                    ></s25-ng-quick-search>
                }
                @if ((waitingForSearch() || tableQuery()) && !isLoading()) {
                    <s25-ng-list-data
                        [hasSelect]="!!editMode()"
                        [query]="tableQuery()!"
                        [actions]="actions()"
                        [(selectedItems)]="selectedItems"
                        [keepSelectionsOnPageChange]="true"
                        [showActions]="waitingForSearch() ? 'top' : 'both'"
                        (primaryChange)="handlePrimaryChange($event)"
                    ></s25-ng-list-data>
                }
            }
        }
    `,
    styles: `
        :host {
            display: flex;
            flex-direction: column;
            gap: 0.25em;
            padding: 1em;
            max-height: 600px;
            overflow-y: auto;
        }

        :host.adding-relations {
            min-height: 375px; /*Make sure the search dropdown is not cut off*/
        }

        :host.adding-relations s25-ng-list-data ::ng-deep .actions-tray {
            justify-content: flex-start;
            gap: 0.5em;
        }

        .profile-picker {
            gap: 0.5rem;
        }

        .actionItems {
            display: flex;
            gap: 1em;
            justify-content: center;
        }

        s25-ng-quick-search {
            width: 100%;
            margin-bottom: 0.25em;
        }

        .ngRed {
            font-size: 90%;
            width: fit-content;
            margin: 0 auto;
        }
    `,
    host: {
        "[class.adding-relations]": "editMode() === 'add'",
        "[class.removing-relations]": "editMode() === 'remove'",
    },
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        S25ListDataComponent,
        S25QuickSearchComponent,
        S25LoadingInlineStaticComponent,
        S25EditableModule,
        FormsModule,
    ],
})
export class S25MoreActionsRelatedComponent {
    readonly itemId = input.required<number>();
    readonly addRemoveType = input.required<"relatedEvents" | "boundReservations">();
    readonly itemTypeId = input<Item.Id>();

    readonly listData = viewChild(S25ListDataComponent);

    // TODO: pull from event?
    readonly itemName = s25Resource({
        params: () => this.itemId(),
        loader: ({ params }) => EventService.getEventNameOrTitle(params),
    });
    readonly eventWithRelationships = s25Resource({
        if: () => this.addRemoveType() === "relatedEvents",
        params: () => this.itemId(),
        loader: ({ params }) => EventService.getEventRelationships(params),
    });
    readonly setMeta = s25Resource({
        if: () => this.addRemoveType() === "relatedEvents",
        params: () => this.itemId(),
        loader: async ({ params }) => EventService.getRelatedEventSet(params),
    });
    readonly eventWithBindings = s25Resource({
        if: () => this.addRemoveType() === "boundReservations",
        params: () => this.itemId(),
        loader: ({ params }) => EventService.getEventBindings(params),
    });
    readonly isLoading = computed(
        () =>
            this.itemName.resource.isLoading() ||
            this.eventWithRelationships.resource.isLoading() ||
            this.setMeta.resource.isLoading() ||
            this.eventWithBindings.resource.isLoading(),
    );

    readonly searchTerm = signal<string | undefined>(undefined);
    readonly selectedSearch = signal<DropDownItem | undefined>(undefined);
    readonly chosenProfile = linkedSignal<EventProfile | undefined>(() => {
        if (this.profiles()?.length === 1) {
            return this.profiles()![0];
        } else {
            return undefined;
        }
    });
    readonly editMode = linkedSignal({
        // reset if chosen profile changes
        source: this.chosenProfile,
        computation: () => false as "add" | "remove" | false,
    });
    readonly errorMsg = linkedSignal({
        // reset whenever edit mode changes
        source: this.editMode,
        computation: () => "",
    });
    readonly search = linkedSignal<QuickSearch | undefined>(() => (this.editMode() === "add" ? {} : undefined));
    readonly showSelectedOnly = linkedSignal<boolean | undefined>(() => (this.search() ? false : undefined));
    readonly selectedItems = linkedSignal({
        // reset selections whenever edit mode and search change
        source: () => `${this.editMode()}:${this.search()?.selectedSearch ?? ""}:${this.search()?.searchTerm ?? ""}`,
        computation: () => new Set<AddRemoveUtil.ContextId | number>(),
    });

    readonly searchCommitted = computed(() => this.search()?.searchTerm || this.search()?.selectedSearch);
    readonly isRemoving = computed(() => this.editMode() === "remove");
    readonly waitingForSearch = computed(() => this.editMode() === "add" && !this.searchCommitted());
    readonly table = computed(() => this.listData()?.tableComponent());
    readonly requiresProfile = computed(() => this.addRemoveType() === "boundReservations");
    readonly itemNameWithLocator = computed(() => {
        if (this.itemName.resource.isLoading()) return;
        const event = this.eventWithRelationships.value() ?? this.eventWithBindings.value();
        if (event) {
            return `${this.itemName.value()} (${event.event_locator})`;
        } else {
            return this.itemName.value();
        }
    });
    readonly profiles = computed(() => this.eventWithBindings.value()?.profile);
    readonly itemIds = computed(() => {
        if (this.addRemoveType() === "boundReservations") {
            if (this.chosenProfile()) {
                return S25Util.array.unique(
                    this.chosenProfile()?.binding_reservation?.map((rsrv) => rsrv.bound_event_id) ?? [],
                );
            } else {
                return;
            }
        } else {
            return this.eventWithRelationships
                .value()
                ?.content?.map((relatedEvent) => S25Util.parseInt(relatedEvent.content_event_id));
        }
    });
    readonly eventIds = computed(() => {
        const unique = new Set(this.itemIds() || []);
        unique.add(this.itemId());
        return [...unique];
    });
    readonly profileIds = computed(() => {
        if (this.profiles()?.length) {
            const profileArr = this.chosenProfile() ? [this.chosenProfile()!.profile_id] : [];
            const profile = this.chosenProfile() || this.profiles()![0];
            if (profile.binding_reservation) {
                profileArr.push(...profile.binding_reservation.map((obj) => obj.bound_reservation_id));
            }
            const uniqueProfiles = S25Util.array.unique(profileArr);
            if (uniqueProfiles.length > 1) {
                return uniqueProfiles.slice();
            }
        }
        return [];
    });
    readonly queryUrl = computed(() => this.computeQueryUrl());
    readonly tableQuery = computed<ListDataParams2 | undefined>(() => {
        if (!this.queryUrl()) return undefined;
        // TODO: figure out a way to query this before the component is rendered
        return {
            itemType: "event",
            query: this.queryUrl(),
            page: 0,
            itemsPerPage: 100,
            comptype:
                this.addRemoveType() === "boundReservations" ? "bound-reservation-quick-search" : "event-quick-search",
            useCache: true,
        };
    });
    readonly lang = computed(() => this.LANG[this.addRemoveType()]);
    readonly actions = computed<Action[]>(() => this.computeActions());

    constructor() {
        effect(() => {
            const table = this.table();
            if (table) {
                if (this.isRemoving()) {
                    table!.setVisibleColumns(
                        new Set(
                            table!.dataSource.columns
                                .map((col) => col.id)
                                .filter((colId) => colId !== ListService.IsPrimaryCol),
                        ),
                    );
                } else {
                    table!.setVisibleColumns(new Set(table!.dataSource.columns.map((col) => col.id)));
                }
            }
        });
        effect(() => {
            if (this.search() === undefined) {
                this.searchTerm.set(undefined);
                this.selectedSearch.set(undefined);
            }
        });
    }

    handlePrimaryChange(contextId: AddRemoveUtil.ContextId) {
        const { event_id, profile_id } = AddRemoveUtil.extractEventProfileId(contextId);
        S25Assert.isDefined(profile_id, "Profile ID must be defined for setting primary bound event.");
        return EventService.setPrimaryBoundEvent(event_id, profile_id);
    }

    @Bind
    saveSetName(newName: string) {
        return EventService.relateEvents([], this.itemId(), undefined, newName);
    }

    private refresh() {
        this.itemName.resource.reload();
        this.eventWithRelationships.resource.reload();
        this.eventWithBindings.resource.reload();
        this.setMeta.resource.reload();
    }

    commitSearch() {
        this.search.set({
            searchTerm: this.searchTerm()?.trim(),
            selectedSearch: this.selectedSearch()?.val,
        });
    }

    private async removeSelected(table: S25TableComponent) {
        if (this.addRemoveType() === "boundReservations") {
            const bindingIds = Array.from(table.selected).map((id: AddRemoveUtil.ContextId) =>
                AddRemoveUtil.extractEventProfileId(id),
            );
            return !!(await EventService.unbindEvents(bindingIds));
        } else {
            const selectedIds = Array.from(table.selected) as number[];
            return !!(await EventService.unrelateEvents(selectedIds));
        }
    }

    private async addSelected(table: S25TableComponent) {
        if (this.addRemoveType() === "boundReservations") {
            S25Assert.isDefined(this.chosenProfile());
            const thisItemId = { event_id: this.itemId(), profile_id: this.chosenProfile()!.profile_id };
            const bindingIds = [
                thisItemId,
                ...Array.from(table.selected).map((id: AddRemoveUtil.ContextId) =>
                    AddRemoveUtil.extractEventProfileId(id),
                ),
            ];
            const resp = await EventService.bindEvents(bindingIds, thisItemId);
            const returnCode = S25Util.isDefined(resp?.root?.ret) ? S25Util.parseInt(resp?.root?.ret) : 0;
            if (returnCode === 0) {
                return true;
            } else if (returnCode === 2) {
                if (await AlertService.confirm(resp.root.msg)) {
                    return !!(await EventService.bindEvents(bindingIds, thisItemId, 1));
                } else {
                    return false;
                }
            } else {
                throw new Error(S25Util.errorText(resp, "Failed to bind events."));
            }
        } else {
            const selectedIds = Array.from(table.selected) as number[];
            return EventService.relateEvents(selectedIds, this.itemId(), undefined);
        }
    }

    private computeActions(): Action[] {
        if (this.editMode() === "add") {
            const actions: Action[] = [];
            actions.push({
                type: "outline",
                text: computed(() => "Search"),
                subText: computed(() => ""),
                activateOnChecked: false,
                action: async () => this.commitSearch(),
            });
            actions.push({
                type: "outline",
                text: computed(() => "Clear Search"),
                subText: computed(() => ""),
                activateOnChecked: false,
                action: async () => this.clearSearch(),
            });
            if (this.searchCommitted()) {
                if (this.showSelectedOnly()) {
                    actions.push({
                        type: "outline",
                        text: computed(() => "Show All"),
                        subText: computed(() => ""),
                        action: async () => this.showSelectedOnly.set(false),
                        activateOnChecked: false,
                    });
                } else {
                    actions.push({
                        type: "outline",
                        text: computed(() => "Show Selected"),
                        subText: computed(() => ""),
                        action: async () => this.showSelectedOnly.set(true),
                        activateOnChecked: true,
                    });
                }
                actions.push({
                    type: "outline",
                    text: computed(() => "Add Selected"),
                    subText: computed(() => ""),
                    action: async (table: S25TableComponent) => {
                        try {
                            if (await this.addSelected(table)) {
                                this.editMode.set(false);
                                this.refresh();
                            }
                        } catch (err) {
                            this.errorMsg.set(
                                `Unable to ${this.addRemoveType() === "boundReservations" ? "bind" : "related"} events: ${err}`,
                            );
                        }
                    },
                    activateOnChecked: true,
                });
            }
            return actions;
        } else if (this.editMode() === "remove") {
            return [
                {
                    type: "outline",
                    text: computed(() => "Remove Selected"),
                    subText: computed(() =>
                        this.addRemoveType() === "boundReservations"
                            ? "Removing bindings can retroactively cause conflicts because bindings are excluded from conflict checking."
                            : "",
                    ),
                    action: async (table) => {
                        try {
                            if (await this.removeSelected(table)) {
                                this.editMode.set(false);
                                this.refresh();
                            }
                        } catch (err) {
                            this.errorMsg.set(`Unable to remove events: ${err}`);
                        }
                    },
                    activateOnChecked: true,
                },
            ];
        }
        return [];
    }

    clearSearch() {
        this.searchTerm.set(undefined);
        this.selectedSearch.set(undefined);
        this.commitSearch();
    }

    private computeQueryUrl() {
        if (this.editMode() !== "add") {
            return this.eventIds().length > 1 || this.profileIds()?.length > 1
                ? this.eventProfileQueryString(this.eventIds(), this.profileIds())
                : undefined;
        } else {
            let queryString: string = "";
            if (this.search()?.searchTerm) {
                queryString += `&name=${encodeURIComponent(this.search()!.searchTerm!)}`;
            }
            if (this.search()?.selectedSearch) {
                queryString += this.search()!.selectedSearch!;
            }
            if (this.showSelectedOnly() && this.selectedItems()) {
                const { eventIds, profileIds } = AddRemoveUtil.extractAllEventAndProfileIds(
                    Array.from(this.selectedItems()),
                );
                queryString += "&" + this.eventProfileQueryString(eventIds, profileIds);
            }
            if (queryString.length) {
                // substring(1) to chop off the first &
                return queryString.substring(1);
            }
        }
    }

    private eventProfileQueryString(eventIds: number[], profileIds?: number[]) {
        // sort IDs so the string can be memoized
        const eventString = `event_id=${[...eventIds].sort().join("+")}`;
        if (profileIds?.length) {
            // sort IDs so the string can be memoized
            return `${eventString}&profile_id=${[...profileIds].sort().join("+")}`;
        } else {
            return eventString;
        }
    }

    // Lang definitions (leaving for bound reservations migration)
    private readonly LANG = {
        boundReservations: {
            defaultErrMsg:
                "You do not have sufficient privileges to alter relationships in this bound event set. Please contact your administrator.",
            searchLabel: "Find Events to Bind:",
            notInSet: "is not currently in a bound event set",
            inSet: "is in a bound event set",
            comptype: "bound-reservation-quick-search",
        },
        relatedEvents: {
            defaultErrMsg:
                "You do not have sufficient privileges to alter relationships in this related event set. Please contact your administrator.",
            searchLabel: "Find Events to Relate:",
            notInSet: "is not currently in a related event set",
            inSet: "is in a related event set",
            comptype: "event-quick-search",
        },
    } as const;
}
